import React, { useState } from 'react'
import { Button } from './button'
import { Avatar, AvatarFallback, AvatarImage } from './avatar'
import { MessageCircle, ThumbsUp, ThumbsDown, X } from 'lucide-react'

interface CoachLogicProps {
  message: string
  suggestions?: string[]
  className?: string
  variant?: 'default' | 'compact' | 'inline'
  showAvatar?: boolean
  onDismiss?: () => void
  onFeedback?: (helpful: boolean, feedbackText?: string) => void
  onSuggestionClick?: (suggestion: string) => void
}

export function CoachLogic({
  message,
  suggestions = [],
  className = '',
  variant = 'default',
  showAvatar = true,
  onDismiss,
  onFeedback,
  onSuggestionClick,
}: CoachLogicProps) {
  const [dismissed, setDismissed] = useState(false)
  const [feedbackGiven, setFeedbackGiven] = useState<boolean | null>(null)
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false)
  const [feedbackText, setFeedbackText] = useState('')

  const handleDismiss = () => {
    setDismissed(true)
    onDismiss?.()
  }

  const handleFeedback = (helpful: boolean) => {
    if (!helpful) {
      setShowFeedbackDialog(true)
    } else {
      setFeedbackGiven(true)
      onFeedback?.(true)
    }
  }

  const handleFeedbackSubmit = () => {
    setFeedbackGiven(false)
    onFeedback?.(false, feedbackText)
    setShowFeedbackDialog(false)
    setFeedbackText('')
  }

  if (dismissed) {
    return null
  }

  const baseStyles = {
    card: {
      border: '1px solid #E5E7EB',
      borderRadius: '0.5rem',
      backgroundColor: '#FFFFFF',
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    },
    header: {
      padding: '1rem 1rem 0.5rem 1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
    },
    content: {
      padding: '0.5rem 1rem',
    },
    footer: {
      padding: '0.5rem 1rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderTop: '1px solid #E5E7EB',
    },
    avatar: {
      width: '2.5rem',
      height: '2.5rem',
      borderRadius: '50%',
      backgroundColor: '#705AF8',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.875rem',
      fontWeight: 'bold',
    },
    titleSection: {
      flex: 1,
    },
    title: {
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#111827',
      margin: 0,
    },
    subtitle: {
      fontSize: '0.75rem',
      color: '#6B7280',
      margin: 0,
    },
    message: {
      fontSize: '0.875rem',
      color: '#374151',
      lineHeight: '1.5',
      margin: 0,
      fontWeight: 500,
    },
    feedbackSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
      fontSize: '0.75rem',
      color: '#6B7280',
    },
    buttonGroup: {
      display: 'flex',
      gap: '0.5rem',
    },
    dismissButton: {
      marginLeft: 'auto',
      padding: '0',
      width: '2rem',
      height: '2rem',
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: '0.25rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#6B7280',
    },
  }

  if (variant === 'inline') {
    return (
      <div
        style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.5rem 0' }}
      >
        {showAvatar && (
          <Avatar style={{ height: '2.5rem', width: '2.5rem' }}>
            <AvatarImage src="/images/CL-avatar.png" alt="Coach Logic" />
            <AvatarFallback
              style={{ backgroundColor: '#3B82F6', color: 'white', fontSize: '0.875rem' }}
            >
              CL
            </AvatarFallback>
          </Avatar>
        )}
        <div style={{ flex: 1 }}>
          <p style={baseStyles.message}>{message}</p>
        </div>
      </div>
    )
  }

  if (variant === 'compact') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '0.75rem',
          backgroundColor: '#F9FAFB',
          borderRadius: '0.5rem',
          border: '1px solid #E5E7EB',
        }}
      >
        {showAvatar && (
          <Avatar style={{ height: '2.5rem', width: '2.5rem' }}>
            <AvatarImage src="/images/CL-avatar.png" alt="Coach Logic" />
            <AvatarFallback
              style={{ backgroundColor: '#3B82F6', color: 'white', fontSize: '0.875rem' }}
            >
              CL
            </AvatarFallback>
          </Avatar>
        )}
        <div style={{ flex: 1 }}>
          <p style={baseStyles.message}>{message}</p>
        </div>
        {onDismiss && (
          <button style={baseStyles.dismissButton} onClick={handleDismiss}>
            <X style={{ width: '1rem', height: '1rem' }} />
          </button>
        )}
      </div>
    )
  }

  return (
    <div className={className}>
      <div style={baseStyles.card}>
        <div style={baseStyles.header}>
          <Avatar
            style={{
              height: '2.5rem',
              width: '2.5rem',
              flexShrink: 0,
            }}
          >
            <AvatarImage src="/images/CL-avatar.png" alt="Coach Logic" />
            <AvatarFallback
              style={{
                backgroundColor: '#3B82F6',
                color: 'white',
                fontSize: '0.875rem',
                fontWeight: 'bold',
              }}
            >
              CL
            </AvatarFallback>
          </Avatar>
          <div style={baseStyles.titleSection}>
            <h4 style={baseStyles.title}>Coach Logic</h4>
            <p style={baseStyles.subtitle}>Your Business AI Coach</p>
          </div>
          {onDismiss && (
            <button style={baseStyles.dismissButton} onClick={handleDismiss}>
              <X style={{ width: '1rem', height: '1rem' }} />
            </button>
          )}
        </div>
        <div style={baseStyles.content}>
          <p style={baseStyles.message}>{message}</p>
        </div>
        {onFeedback && (
          <div style={baseStyles.footer}>
            <div style={baseStyles.feedbackSection}>
              <MessageCircle
                style={{ width: '0.75rem', height: '0.75rem', marginRight: '0.25rem' }}
              />
              Did this help you?
            </div>
            <div style={baseStyles.buttonGroup}>
              <Button
                variant="ghost"
                size="sm"
                style={{
                  height: '1.75rem',
                  padding: '0 0.5rem',
                  backgroundColor: feedbackGiven === true ? '#DCFCE7' : 'transparent',
                  color: feedbackGiven === true ? '#15803D' : '#374151',
                }}
                onClick={() => handleFeedback(true)}
              >
                <ThumbsUp
                  style={{
                    width: '0.75rem',
                    height: '0.75rem',
                    marginRight: '0.25rem',
                    stroke: feedbackGiven === true ? '#15803D' : '#374151',
                    fill: 'none',
                  }}
                />
                Yes
              </Button>
              <Button
                variant="ghost"
                size="sm"
                style={{
                  height: '1.75rem',
                  padding: '0 0.5rem',
                  backgroundColor: feedbackGiven === false ? '#FEE2E2' : 'transparent',
                  color: feedbackGiven === false ? '#DC2626' : '#374151',
                }}
                onClick={() => handleFeedback(false)}
              >
                <ThumbsDown
                  style={{
                    width: '0.75rem',
                    height: '0.75rem',
                    marginRight: '0.25rem',
                    stroke: feedbackGiven === false ? '#DC2626' : '#374151',
                    fill: 'none',
                  }}
                />
                No
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Feedback Dialog */}
      {showFeedbackDialog && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '1.5rem',
              borderRadius: '0.5rem',
              maxWidth: '28rem',
              width: '90%',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            }}
          >
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem' }}>
              Help me do better
            </h3>
            <p style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '1rem' }}>
              I'd like to know why that wasn't helpful so I can give you better advice next time.
            </p>
            <textarea
              placeholder="What would have been more helpful to you?"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              style={{
                width: '100%',
                minHeight: '6rem',
                padding: '0.75rem',
                border: '1px solid #D1D5DB',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                marginBottom: '1rem',
                resize: 'vertical',
              }}
            />
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <Button
                variant="outline"
                onClick={() => setShowFeedbackDialog(false)}
                style={{ padding: '0.5rem 1rem' }}
              >
                Skip
              </Button>
              <Button
                onClick={handleFeedbackSubmit}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#705AF8',
                  color: 'white',
                  border: 'none',
                }}
              >
                Send
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
